<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import cloneDeep from 'lodash/cloneDeep'
import VGlobalSearchFilterItem from './VGlobalSearchFilterItem.vue'
import { VIcons } from '@/shared/ui'
import { PresetSave } from '@/features/GlobalSearch/PresetSave'
import { useLoadSearchPresets } from '@/entities/GlobalSearch/PresetTable/model/api.ts'
import type { FilterCondition, SearchPresetItem } from './../model/type.ts'
import { PresetDeleteButton } from '@/features/GlobalSearch/PresetDelete'
import { PresetClearButton } from '@/features/GlobalSearch/PresetClear'
import { createFilterKey } from '../model/helpers.ts'
import { emptyValueForCompareType } from '../model/filterConditionUtils.ts'
import { useTranslate } from '@/shared/lib'

const props = defineProps({
  objectTypeId: {
    type: Number,
    default: 0,
  },
})

const { trs } = useTranslate()

/**
 * Черновик фильтра — пробрасывается из WGlobalSearchAdvance через v-model:filter.
 * Изменения здесь не уходят напрямую в model.query: они применяются
 * только при нажатии кнопки "Найти" через searchHandler родителя.
 */
const filter = defineModel<FilterCondition[][]>('filter', { default: () => [] })

/*
 * Локальное состояние групп фильтра.
 * Каждый вложенный массив — одна OR-группа условий.
 */
const assignKeys = (g: FilterCondition[][]): FilterCondition[][] =>
  g.map((group) => group.map((c) => ({ ...c, _key: c._key ?? createFilterKey() })))

const groups = ref<FilterCondition[][]>(assignKeys(cloneDeep(filter.value)))
let syncingToFilter = false

watch(
  groups,
  (val) => {
    syncingToFilter = true
    filter.value = cloneDeep(val)
    nextTick(() => {
      syncingToFilter = false
    })
  },
  { deep: true },
)

watch(
  filter,
  (val) => {
    if (syncingToFilter) return

    groups.value = assignKeys(cloneDeep(val))
    selectedPresetId.value = null
  },
  { deep: true },
)

/* ─── Пресеты ─────────────────────────────────────────────────────────────── */

const { data: presetsData } = useLoadSearchPresets()
const selectedPresetId = ref<number | null>(null)

/*
 * Список пресетов напрямую из ответа сервера — данные уже в формате SearchPresetItem.
 */
const presetItems = computed<SearchPresetItem[]>(() => {
  return [
    { id: null, name: trs('itf.empty_preset'), items: [] },
    ...(presetsData.value?.data ?? []),
  ]
})

/*
 * Применяет выбранный пресет — заменяет текущие группы фильтра
 * глубокой копией условий из пресета.
 */
const applyPreset = (id: number | null) => {
  const preset = presetItems.value.find((p) => p.id === id)
  if (!preset) return
  groups.value = assignKeys(
    cloneDeep(preset.items).map((group) =>
      group.map((condition) => ({
        ...condition,
        sort: condition.sort ?? null,
      })),
    ),
  )
}

watch(selectedPresetId, applyPreset)

/* ─── Управление группами и условиями ──────────────────────────────── */

/*
 * Создаёт пустое условие с текущим черновым типом объекта.
 */
const makeCondition = (): FilterCondition => ({
  _key: createFilterKey(),
  object_type_id: props.objectTypeId || 0,
  field_id: '',
  compare_type: null,
  value: emptyValueForCompareType(null),
  sort: null,
})

/*
 * Добавляет новую OR-группу с одним пустым условием.
 */
const addGroup = () => {
  groups.value.push([makeCondition()])
}

/*
 * Удаляет OR-группу по индексу.
 */
const deleteGroup = (gIdx: number) => {
  groups.value.splice(gIdx, 1)
}

/*
 * Добавляет пустое условие в конец указанной группы.
 */
const addCondition = (gIdx: number) => {
  groups.value[gIdx].push(makeCondition())
}

/*
 * Удаляет условие из группы. Если группа после удаления становится пустой —
 * удаляет и саму группу.
 */
const deleteCondition = (gIdx: number, cIdx: number) => {
  groups.value[gIdx].splice(cIdx, 1)
  if (groups.value[gIdx].length === 0) groups.value.splice(gIdx, 1)
}

/* ─── Drag & Drop ─────────────────────────────────────────────────── */

interface DragState {
  fromG: number
  fromC: number
  overG: number
  overC: number
}

const dragState = ref<DragState | null>(null)

/*
 * Запоминает исходную позицию перетаскиваемого элемента
 * и инициализирует тип операции в dataTransfer.
 */
const onDragStart = (gIdx: number, cIdx: number, e: DragEvent) => {
  dragState.value = { fromG: gIdx, fromC: cIdx, overG: gIdx, overC: cIdx }
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', '')
  }
}

/*
 * Обновляет текущую позицию наведения во время перетаскивания.
 */
const onDragOver = (gIdx: number, cIdx: number) => {
  if (dragState.value) {
    dragState.value.overG = gIdx
    dragState.value.overC = cIdx
  }
}

/*
 * Выполняет перестановку условий внутри группы при сбросе элемента.
 * Перетаскивание между разными группами не поддерживается.
 */
const onDrop = (toGIdx: number, toCIdx: number) => {
  if (!dragState.value) return
  const { fromG, fromC } = dragState.value
  if (fromG !== toGIdx || fromC === toCIdx) return
  const [moved] = groups.value[fromG].splice(fromC, 1)
  groups.value[fromG].splice(toCIdx, 0, moved)
  dragState.value = null
}

/*
 * Сбрасывает состояние drag & drop по окончании сессии перетаскивания.
 */
const onDragEnd = () => {
  dragState.value = null
}

/*
 * Возвращает true, если элемент является активной целью наведения,
 * но не самим перетаскиваемым элементом.
 */
const isDragOver = (gIdx: number, cIdx: number): boolean => {
  if (!dragState.value) return false
  const { fromG, fromC, overG, overC } = dragState.value
  return overG === gIdx && overC === cIdx && !(fromG === gIdx && fromC === cIdx)
}
</script>

<template>
  <div>
    <div class="advanced-search-divider my-6">
      <VDivider>
        {{ $t('itf.advanced_search') }}
      </VDivider>
    </div>

    <div class="w-global-search__preset-row">
      <VSelect
        v-model="selectedPresetId"
        :items="presetItems"
        :label="$t('itf.list_preset')"
        item-value="id"
        item-title="name"
        hide-details
        clearable
        density="compact"
        class="w-global-search__preset-select"
      />

      <PresetSave v-model="groups" :presets="presetItems" :preset-id="selectedPresetId" />

      <PresetDeleteButton
        :presets="presetItems"
        :preset-id="selectedPresetId"
        @deleted="selectedPresetId = null"
      />

      <PresetClearButton v-model:selectedPreset="selectedPresetId" v-model:groups="groups" />
    </div>

    <div v-if="groups.length" class="mt-4">
      <template v-for="(group, gIdx) in groups" :key="gIdx">
        <div v-if="gIdx > 0" class="w-global-search__group-or">
          <span>{{ $t('itf.OR') }}</span>
        </div>

        <div class="w-global-search__group">
          <div
            v-for="(condition, cIdx) in group"
            :key="condition._key"
            draggable="true"
            class="w-global-search__drag-item"
            :class="{ 'is-drag-over': isDragOver(gIdx, cIdx) }"
            @dragstart="onDragStart(gIdx, cIdx, $event)"
            @dragover.prevent="onDragOver(gIdx, cIdx)"
            @drop.prevent="onDrop(gIdx, cIdx)"
            @dragend="onDragEnd"
          >
            <VGlobalSearchFilterItem
              :filter="condition"
              :global-object-type-id="objectTypeId"
              @update:filter="groups[gIdx][cIdx] = $event"
              @delete="deleteCondition(gIdx, cIdx)"
            />
          </div>

          <div class="w-global-search__bts mt-2">
            <VTooltip location="top" :text="$t('itf.add_preset_condition')">
              <template #activator="{ props }">
                <VBtn variant="text" density="compact" @click="addCondition(gIdx)" v-bind="props">
                  <VIcons type="add" />
                </VBtn>
              </template>
            </VTooltip>

            <VTooltip location="top" :text="$t('itf.delete_preset_group')">
              <template #activator="{ props }">
                <VBtn
                  variant="text"
                  color="error"
                  density="compact"
                  @click="deleteGroup(gIdx)"
                  v-bind="props"
                >
                  <VIcons type="close" />
                </VBtn>
              </template>
            </VTooltip>
          </div>
        </div>
      </template>
    </div>

    <div class="w-global-search__bts mt-2">
      <VTooltip location="top" :text="$t('itf.add_preset_group')">
        <template #activator="{ props }">
          <VBtn variant="tonal" density="compact" @click="addGroup" v-bind="props">
            <VIcons type="add" />
          </VBtn>
        </template>
      </VTooltip>
    </div>

    <VDivider class="mt-2 mb-4" />
  </div>
</template>
